import * as React from 'react';
import { AxiosError } from 'axios';
import { saveAs } from 'file-saver';
import { CardElement, PaymentRequestButtonElement } from 'react-stripe-elements';
import { find } from 'lodash';

import { InvoiceAuthModel } from '../../../models/invoice-auth.model';
import { Modal } from '../../../components/modal/modal.component';
import { Invoice } from '../../../models/invoice.model';
import { Merchant } from '../../../models/merchant.model';
import { Payment } from '../../../models/payment.model';
import { SuccessComponent } from './success/success.component';
import { ErrorComponent } from './error/error.component';
import { displayAmount, convertToCents } from '../../../helpers/currency';
import { CurrencyInput } from './currency-input/currency-input.component';
import * as styles from './payment-page.component.styles';

import { ReactComponent as AttachmentIcon } from '../../../images/wv-icon-folder.svg';
import { ReactComponent as LockIcon } from '../../../images/wv-icon-lock.svg';
import { ReactComponent as StripeIcon } from '../../../images/powered-by-stripe.svg';
import VisaIcon from '../../../images/visa.png';
import DiscoverIcon from '../../../images/discover.png';
import AmexIcon from '../../../images/amex.png';
import MastercardIcon from '../../../images/mastercard.png';
import LoadingSpinner from '../../../images/loading-spinner.gif';

// Adding this because the typings package is missing a field
interface StripePaymentResponse extends PaymentResponse {
  readonly token: any;
}

interface Props {
  error?: AxiosError;
  success: boolean;
  invoice?: Invoice;
  logo: string;
  stripe: any;
  uniqueLink: string;
  merchant?: Merchant;
  attachment?: Blob;
  auth: InvoiceAuthModel;
  loading: boolean;
  declined: boolean;

  getInvoice: (uniqueCode: string, birthdate: string) => void;
  submitPayment: (
    invoiceId: string,
    payment: Payment,
    complete?: (reason?: 'success' | 'fail' | 'unknown') => void
  ) => void;
}

interface State {
  payment: Payment;
  isConfirmModalOpen: boolean;
  isDeclinedModalOpen: boolean;
  cardErrorMessage?: string;
  submitAttempted: boolean;
  canUseWallet: boolean;
  paymentRequest?: any;
  devMode?: boolean; // TODO: Used to test new features since we don't have a real dev environment yet
}

export class PaymentPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // TODO: Dev mode
    const params = new URLSearchParams(window.location.search);
    const devMode = !!params.get('devMode');

    this.state = {
      isConfirmModalOpen: false,
      isDeclinedModalOpen: false,
      payment: {
        amount: 0,
        cardName: '',
        receiptEmail: '',
        token: '',
      },
      submitAttempted: false,
      canUseWallet: false,
      devMode,
    };
  }

  componentDidMount() {
    this.props.getInvoice(this.props.uniqueLink, '');
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.invoice && this.props.invoice !== prevProps.invoice) {
      const payment = { ...this.state.payment, amount: this.props.invoice.amount };

      const paymentRequest = this.props.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: `Payment`,
          amount: convertToCents(this.state.payment.amount),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      paymentRequest.on(
        'token',
        ({ complete, token, ...data }: StripePaymentResponse) => {
          try {
            const newPayment = {
              amount: this.state.payment.amount,
              token: token.id,
              cardName: token.card.name,
              receiptEmail: data.payerEmail || this.state.payment.receiptEmail,
            };
            this.props.submitPayment(this.props.invoice!.id, newPayment, complete);
          } catch (error) {
            console.error(error);
            complete('fail');
          }
        }
      );

      paymentRequest.canMakePayment().then((result: any) => {
        this.setState({ canUseWallet: !!result });
      });

      this.setState({ payment, paymentRequest });
    }

    if (this.props.declined && this.props.declined !== prevProps.declined) {
      this.setState({ isDeclinedModalOpen: true });
    }
  }

  downloadAttachment = () => {
    if (this.props.attachment) {
      saveAs(this.props.attachment);
    }
  };

  handleAmountChange = (value: number) => {
    const payment = { ...this.state.payment, amount: value };
    this.setState({ payment });
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const payment = { ...this.state.payment, [name]: value };
    this.setState({ payment });
  };

  handleCardChange = (event: any) => {
    if (this.state.submitAttempted) {
      const cardErrorMessage = event.error ? event.error.message : '';
      this.setState({ cardErrorMessage });
    }
  };

  validate = () => {
    return (
      this.props.invoice &&
      this.state.payment.amount >= 0.5 &&
      this.state.payment.amount <= this.props.invoice.amount &&
      this.state.payment.cardName.length
    );
  };

  confirm = async (event: React.FormEvent) => {
    event.preventDefault();

    if (this.validate()) {
      const result = await this.props.stripe.createToken({
        name: this.state.payment.cardName,
      });
      if (result.token) {
        const payment = { ...this.state.payment, token: result.token.id };
        this.setState({ payment, isConfirmModalOpen: true });
      } else {
        const cardErrorMessage = result.error ? result.error.message : 'Error';
        this.setState({ submitAttempted: true, cardErrorMessage });
      }
    }
  };

  closeModals = () => {
    this.setState({ isConfirmModalOpen: false, isDeclinedModalOpen: false });
  };

  submit = () => {
    this.closeModals();
    this.props.submitPayment(this.props.invoice!.id, this.state.payment);
  };

  getDeclinedErrorMessage = () => {
    const defaultMessage = 'The credit card you entered has been declined.';
    try {
      return this.props.error!.response!.data.error.message || defaultMessage;
    } catch {
      return defaultMessage;
    }
  };

  isExpress = () => {
    const activeProcessor =
      this.props.merchant && find(this.props.merchant.processors, 'isActive');
    return !!(activeProcessor && activeProcessor.stripeAccountType === 'EXPRESS');
  };

  renderBody = () => {
    if (this.props.success) {
      return (
        <SuccessComponent merchant={this.props.merchant!} payment={this.state.payment} />
      );
    } else if (this.props.error && !this.props.declined) {
      return (
        <ErrorComponent
          auth={this.props.auth}
          merchant={this.props.merchant}
          error={this.props.error}
          uniqueLink={this.props.uniqueLink}
          getInvoice={this.props.getInvoice}
        />
      );
    } else {
      return (
        <>
          {this.props.invoice && this.props.invoice.id && this.props.merchant && (
            <form onSubmit={this.confirm}>
              <div className={styles.requestLabel}>
                Requested Amount: {displayAmount(this.props.invoice.amount)}
              </div>
              <div className={styles.amountRow}>
                <div>
                  <span className={styles.boldLabel}>Payment Amount</span>
                  <span className="amountSymbol">$</span>
                  <CurrencyInput
                    className="amountBox"
                    value={this.state.payment.amount}
                    onChange={this.handleAmountChange}
                    required={true}
                    min={0.5}
                    max={this.props.invoice.amount}
                  />
                  <span className={styles.smallText}>Amount can be modified</span>
                </div>
                {this.props.invoice.hasAttachment && this.props.attachment && (
                  <div className="attachmentBox">
                    <span className={styles.boldLabel}>Billing Statement</span>
                    <div
                      onClick={this.downloadAttachment}
                      className={styles.attachmentLink}
                    >
                      <AttachmentIcon className={styles.attachmentIcon} />
                      View Invoice
                    </div>
                  </div>
                )}
              </div>

              {this.state.canUseWallet && this.isExpress() && (
                <div className={styles.paymentRequestButton}>
                  <PaymentRequestButtonElement
                    paymentRequest={this.state.paymentRequest}
                    onClick={() => {
                      this.state.paymentRequest.update({
                        total: {
                          amount: convertToCents(this.state.payment.amount),
                          label: `Payment to ${this.props.merchant!.name}`,
                        },
                      });
                    }}
                  />
                </div>
              )}

              <div className={styles.creditCardLabel}>Credit Card</div>
              <input
                name="cardName"
                type="text"
                placeholder="Cardholder's Name"
                onChange={this.handleChange}
                required={true}
              />
              <CardElement
                onChange={this.handleCardChange}
                className={styles.cardField}
              />
              {this.state.cardErrorMessage && (
                <span className={styles.cardErrorText}>
                  {this.state.cardErrorMessage}
                </span>
              )}
              <div className={styles.cardLogos}>
                <img src={VisaIcon} alt="Visa" />
                <img src={MastercardIcon} alt="Mastercard" />
                <img src={DiscoverIcon} alt="Discover" />
                <img src={AmexIcon} alt="American Express" />
              </div>
              <input
                name="receiptEmail"
                type="email"
                placeholder="Receipt Email"
                className={styles.emailField}
                onChange={this.handleChange}
              />

              <div className={styles.buttonContainer}>
                <button type="submit" className={styles.button}>
                  <span style={{ verticalAlign: 'middle' }}>Pay Now</span>
                  <LockIcon className={styles.lockIcon} />
                </button>
                <div>
                  <a
                    className={styles.smallText}
                    href="https://stripe.com/us/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  <StripeIcon className={styles.stripeIcon} />
                </div>
              </div>
            </form>
          )}
        </>
      );
    }
  };

  render() {
    if (this.props.loading) {
      return (
        <div className={styles.loaderContainer}>
          <img src={LoadingSpinner} className={styles.loader} alt="loading" />
        </div>
      );
    }

    return (
      <div className={styles.container}>
        {this.props.logo && (
          <div className={styles.logoContainer}>
            <img src={this.props.logo} alt="logo" />
          </div>
        )}
        {this.renderBody()}
        <Modal
          className={styles.modal}
          onRequestClose={this.closeModals}
          isOpen={this.state.isDeclinedModalOpen}
        >
          <h4>Processing Error</h4>
          <p>
            {this.getDeclinedErrorMessage()}
            <br />
            Please try again or use different card.
          </p>
          <button className={styles.button} onClick={this.closeModals}>
            Close
          </button>
        </Modal>
        {this.props.merchant && (
          <>
            <Modal
              className={styles.modal}
              onRequestClose={this.closeModals}
              isOpen={this.state.isConfirmModalOpen}
            >
              <h4>Confirm Payment</h4>
              <div className={styles.confirmAmountLabel}>
                {displayAmount(this.state.payment.amount)}
              </div>
              <p>
                Click "Confirm" to submit your
                <br /> payment to <strong>{this.props.merchant.name}</strong>.{' '}
                <p>Please allow time for processing.</p>
              </p>
              <button className={styles.button} onClick={this.submit}>
                Confirm
              </button>
            </Modal>
          </>
        )}
      </div>
    );
  }
}
